/* eslint-disable @typescript-eslint/no-explicit-any */
import { AppDate } from '../envTypes';
import { ModelBase, Platform } from './base';

export enum EmailTemplate {
  'eventPurchase' = 'eventPurchase',
  'noShippoBuyer' = 'noShippoBuyer',
  'orderBuyer' = 'orderBuyer',
  'orderSeller' = 'orderSeller',
  'orderShipped' = 'orderShipped',
  'orderSummary' = 'orderSummary',
  'refund' = 'refund',
  'transferApproved' = 'transferApproved',
  'transferInitiated' = 'transferInitiated',
  'tuneIn' = 'tuneIn',
  'vendorApplication' = 'vendorApplication',
  'welcome' = 'welcome',
}

export interface Mail {
  from?: string;
  to?: string | string[];
  toUids?: string[];
  cc?: string | string[];
  ccUids?: string[];
  bcc?: string | string[];
  bccUids?: string[];
  headers?: { [key: string]: string };
  message?: {
    subject: string;
    html: string;
    text?: string;
  };
  // template has higher priority than message when both exists
  template?: {
    name: string;
    data?: any;
  };
  externalId?: string;
  context?: {
    recipientUserId: string;
    senderUserId: string;
    platform: Platform;
    subject: string;
    templateName: string;
    /**
     * The creator this email is ABOUT, when that is not the recipient.
     *
     * Half the pop.store catalogue is fan-, buyer- and customer-facing, so the
     * recipient is frequently not the creator whose store the email concerns.
     * Anything reading these documents back to attribute a send — the
     * settled-delivery cron that files `notifications` rows — needs the creator,
     * and `recipientUserId` is the wrong answer for exactly those emails.
     *
     * Optional: only the Email Builder's `flowSend` stamps it, and only when its
     * caller supplied one. A reader falls back to `recipientUserId`.
     */
    creatorId?: string;
    /**
     * `false` when the SENDER is recording this delivery itself, so nothing
     * else should.
     *
     * It has to live on the document rather than stay a property of the send
     * call: the settled-delivery cron reads the `mails` collection, not the
     * request, so an opt-out that never reached the document left the caller's
     * own row and the cron's row both being written, under different keys, for
     * every email. popstore-ai-service's journey sender is the caller that sets
     * it.
     *
     * Absent means "report it", which is the behaviour every existing document
     * already has.
     */
    reportDelivery?: boolean;
    // don't set this
    data?: any;
  };
  createdAt?: AppDate;
  delivery?: {
    state: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'ERROR'| 'RETRY';
    /**
     * The Restate invocation that owns the current PROCESSING claim. Absent on
     * claims made by the firestore-send-email extension.
     *
     * The claim runs inside `ctx.run`, which Restate re-executes if the worker
     * dies after the Firestore transaction commits but before the run is
     * journaled. Recording the owner lets the replay recognise its own claim
     * instead of reading it as another worker's and abandoning the mail in
     * PROCESSING -- a state the ERROR-only retry scan never looks at.
     */
    owner?: string;
    startTime?: AppDate;
    endTime?: AppDate;
    leaseExpireTime?: null | AppDate;
    error?: null | string;
    attempts?: number;
    info?: {
      messageId: string;
      response?: string;
      accepted?: string[];
      rejected?: string[];
      pending?: string[];
    };
  };
}

export enum MailTemplateBodyType {
  Slate = 'SLATE'
}

export interface MailTemplateBody {
  type: MailTemplateBodyType;
  value: string;
  variables?: string[];
}

export interface MailTemplate {
  updatedAt: AppDate;

  messageId?: string;
  subject: string;
  text?: string;
  html?: string;
  amp?: string;

  name?: string;
  description?: string;
  body?: MailTemplateBody;
  platform?: Platform;
  sellerOverridable?: boolean;
}

// /mailTemplates/${templateId}/sellerOverrides/${sellerId}
export interface SellerOverriddenMailTemplate extends ModelBase {
  subject: string;
  body: MailTemplateBody;
  html?: string;
}
